<template>
	<div class="account-card" @click="handleClick">
		<!-- Illustration Section -->
		<div class="card-illustration" :style="{ backgroundColor: illustrationColor }">
			<img v-if="icon" :src="icon" :alt="label" class="illustration-icon" />
		</div>

		<!-- Content Section -->
		<div class="card-content">
			<div class="card-label-row">
				<p class="card-label" v-html="formattedLabel"></p>

				<!-- Account Avatars -->
				<div v-if="accountIcons && accountIcons.length > 0" class="account-avatars">
					<div
						v-for="(iconUrl, index) in displayAvatars"
						:key="index"
						class="avatar"
					>
						<img :src="iconUrl" :alt="`Account ${index + 1}`" @error="handleImageError" />
					</div>
					<div v-if="remainingCount > 0" class="avatar avatar-badge">
						<span class="remaining-count">+{{ remainingCount }}</span>
					</div>
				</div>
			</div>

			<div v-if="balance" class="card-balance-row">
				<p class="card-balance">{{ balance }}</p>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'AccountCard',
	props: {
		label: {
			type: String,
			required: true,
			description: 'Label for the card (e.g., "Insurance", "Pensions")'
		},
		icon: {
			type: String,
			default: null,
			description: 'URL for the illustration icon'
		},
		illustrationColor: {
			type: String,
			default: '#C6CAE5',
			description: 'Background color for the illustration area'
		},
		accountIcons: {
			type: Array,
			default: () => [],
			description: 'Array of icon URLs for linked accounts (max 3 shown)'
		},
		balance: {
			type: String,
			default: null,
			description: 'Balance amount to display (e.g., "£3,722.00")'
		}
	},
	computed: {
		formattedLabel() {
			if (!this.label) return '';
			if (this.balance) return this.label;
			return this.label.replace(/ /g, '<br>');
		},
		displayAvatars() {

			if (!this.accountIcons) return [];
			if (this.accountIcons.length <= 3) {
				return this.accountIcons;
			}
			return this.accountIcons.slice(0, 2);
		},
		remainingCount() {
			if (!this.accountIcons || this.accountIcons.length <= 3) {
				return 0;
			}
			return this.accountIcons.length - 2;
		}
	},
	methods: {
		handleClick() {
			this.$emit('click', { label: this.label });
		},
		handleImageError(event) {
			event.target.src = require('@/assets/round1.png');
		}
	}
}
</script>

<style scoped>
.account-card {
	display: flex;
	flex-direction: column;
	width: 100%;
	border-radius: 8px;
	overflow: hidden;
	background: white;
	box-shadow: 0px 4px 8px 0px rgba(51, 40, 71, 0.1);
	transition: transform 0.2s ease, box-shadow 0.2s ease;
	position: relative;
}

.account-card:hover {
	transform: translateY(-2px);
	box-shadow: 0px 6px 12px 0px rgba(51, 40, 71, 0.15);
}

.card-illustration {
	width: 100%;
	height: 90px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	position: relative;
}

.illustration-icon {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.card-content {
	background: white;
	backdrop-filter: blur(7.5px);
	padding: 8px;
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.card-label-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
}

.card-label {
	font-size: 13px;
	line-height: 16px;
	font-weight: bold;
	margin: 0;
}

.card-balance-row {
	display: flex;
	align-items: flex-start;
	width: 100%;
}

.card-balance {
	font-size: 13px;
	line-height: 16px;
	color: #392a4e;
	margin: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.account-avatars {
	display: flex;
	align-items: center;
	position: absolute;
	right: 8px;
	bottom: 8px;
}

.avatar {
	width: 16.67px;
	height: 16.67px;
	border-radius: 999px;
	overflow: hidden;
	border: 0.417px solid #ccc;
	margin-left: -8.33px;
	position: relative;
	background-color: #f5f5f5;
}

.avatar:first-child {
	margin-left: 0;
}

.avatar img {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}

.avatar-badge {
	background-color: #E8E8E8;
	display: flex;
	align-items: center;
	justify-content: center;
	border: none;
}

.remaining-count {
	font-size: 10px;
	font-weight: bold;
	color: #392a4e;
}</style>
